import PropTypes from 'prop-types';
import React from 'react';
import styled from 'styled-components';
import ButtonV2 from 'components/ButtonV2';

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: ${({ isOpen }) => (isOpen ? 1 : 0)};
  visibility: ${({ isOpen }) => (isOpen ? 'visible' : 'hidden')};
  transition: all 0.2s ease;
`;

const ModalContent = styled.div`
  background: ${({ theme }) =>
    theme.name === 'dark'
      ? 'linear-gradient(180deg, #1a1a2e 0%, #16213e 100%)'
      : '#ffffff'};
  border-radius: 16px;
  padding: 32px;
  max-width: 420px;
  width: 90%;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);
  transform: ${({ isOpen }) => (isOpen ? 'scale(1)' : 'scale(0.95)')};
  transition: transform 0.2s ease;
`;

const IconWrapper = styled.div`
  width: 56px;
  height: 56px;
  border-radius: 14px;
  background: ${({ theme }) =>
    theme.name === 'dark'
      ? 'rgba(239, 68, 68, 0.15)'
      : 'rgba(239, 68, 68, 0.1)'};
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 20px;
  color: #ef4444;

  svg {
    width: 28px;
    height: 28px;
  }
`;

const Title = styled.h3`
  font-size: 20px;
  font-weight: 600;
  color: ${({ theme }) => (theme.name === 'dark' ? '#ffffff' : '#0F172A')};
  margin: 0 0 12px 0;
  text-align: center;
`;

const Message = styled.p`
  font-size: 15px;
  color: ${({ theme }) => (theme.name === 'dark' ? '#94a3b8' : '#64748b')};
  margin: 0 0 28px 0;
  text-align: center;
  line-height: 1.6;
`;

const ItemName = styled.span`
  font-weight: 600;
  color: ${({ theme }) => (theme.name === 'dark' ? '#ffffff' : '#0F172A')};
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;

  button {
    flex: 1;
  }
`;

const TrashIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <polyline points="3 6 5 6 21 6" />
    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
    <line x1="10" y1="11" x2="10" y2="17" />
    <line x1="14" y1="11" x2="14" y2="17" />
  </svg>
);

const DeleteConfirmationModal = ({
  isOpen,
  onClose,
  onConfirm,
  itemName,
  itemType = 'item',
  isDeleting = false,
}) => {
  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget && !isDeleting) {
      onClose();
    }
  };

  // Capitalize first letter of item type for display
  const capitalizedType =
    itemType.charAt(0).toUpperCase() + itemType.slice(1).toLowerCase();

  return (
    <ModalOverlay isOpen={isOpen} onClick={handleOverlayClick}>
      <ModalContent isOpen={isOpen}>
        <IconWrapper>
          <TrashIcon />
        </IconWrapper>
        <Title>Delete {capitalizedType}?</Title>
        <Message>
          Are you sure you want to delete <ItemName>"{itemName}"</ItemName>?
          This action cannot be undone.
        </Message>
        <ButtonGroup>
          <ButtonV2 type="button" onClick={onClose} disabled={isDeleting}>
            Cancel
          </ButtonV2>
          <ButtonV2
            danger
            type="button"
            onClick={onConfirm}
            disabled={isDeleting}
          >
            {isDeleting ? 'Deleting...' : `Delete ${capitalizedType}`}
          </ButtonV2>
        </ButtonGroup>
      </ModalContent>
    </ModalOverlay>
  );
};

DeleteConfirmationModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  itemName: PropTypes.string,
  itemType: PropTypes.string,
  isDeleting: PropTypes.bool,
};

export default DeleteConfirmationModal;
